'use client'

import {useCallback, useEffect, useRef, useState} from 'react'
import {Loader2, Play, Volume2} from 'lucide-react'
import {TTS_VOICE_MODELS, type TtsVoiceModel} from '@/shared'
import type {SettingsState} from '@/types'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import {apiPost} from '@/lib/api'
import {message} from '@/components/Toast'

/**
 * "设置 → 个人设置 → 文字转语音" page. Mirrors the `TTSConfigView` from
 * WechatBotConsole.jsx: a single card with a master toggle, a horizontal
 * row of voice cards, and a preview button that synthesizes the sample
 * line through `/api/tts/synthesize`.
 */
const PREVIEW_TEXT = '你好，这是一条测试语音。'

type SynthesizeResponse = {
    success: boolean
    base64?: string
    mimeType?: string
    format?: 'wav' | 'mp3'
    playLength?: number
}

export function TTSConfigView({
    settings,
    saveSettings,
}: {
    settings: SettingsState
    saveSettings: (patch: Partial<SettingsState>) => Promise<void> | void
}) {
    const tts = settings.tts || {enabled: false, selectedVoiceId: ''}
    const [previewBusy, setPreviewBusy] = useState<string>('')
    const [enabling, setEnabling] = useState(false)
    const audioRef = useRef<HTMLAudioElement | null>(null)

    const stopPreview = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.src = ''
            audioRef.current = null
        }
    }, [])

    useEffect(() => stopPreview, [stopPreview])

    const activeId = tts.selectedVoiceId || TTS_VOICE_MODELS[0]?.id || ''

    async function handleApply(voice: TtsVoiceModel) {
        if (tts.selectedVoiceId !== voice.id) {
            await saveSettings({
                tts: {enabled: tts.enabled, selectedVoiceId: voice.id},
            })
        }
    }

    async function handleToggleEnabled() {
        const next = !tts.enabled
        if (!next) {
            await saveSettings({tts: {...tts, enabled: false}})
            return
        }
        if (enabling) return
        setEnabling(true)
        try {
            await apiPost<{success: boolean}>('/tts/install', {})
            await saveSettings({tts: {...tts, enabled: true}})
        } catch {
            /* toast already fired by api.ts */
        } finally {
            setEnabling(false)
        }
    }

    async function handlePreview(voice: TtsVoiceModel) {
        if (previewBusy) return
        stopPreview()
        setPreviewBusy(voice.id)
        try {
            const response = await apiPost<SynthesizeResponse>('/tts/synthesize', {
                text: PREVIEW_TEXT,
                voice: voice.id,
            })
            if (!response.success || !response.base64) {
                message.error('合成失败')
                return
            }
            const audio = new Audio(response.base64)
            audioRef.current = audio
            audio.play().catch(() => {
                message.error('浏览器阻止了自动播放，请点击页面后再次尝试')
            })
        } catch {
            // toast already fired by api.ts
        } finally {
            setPreviewBusy('')
        }
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="文字转语音 (TTS)"
                description="开启后，系统自动回复的纯文本内容将被转换为语音发送；聊天框可手动转语音。"
            />
            <div className="flex-1 overflow-y-auto p-6">
                <div className="bg-white rounded-card shadow-card border border-hair w-full max-w-5xl p-8">
                    <div className="flex items-center justify-between mb-8 pb-6 border-b border-hair">
                        <div className="flex items-center gap-3">
                            <Volume2 size={24} className="text-accent"/>
                            <h3 className="text-lg font-medium text-ink tracking-wide">
                                TTS 语音合成配置
                            </h3>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center gap-1.5 text-sm text-soft font-medium">
                                {enabling ? (
                                    <>
                                        开启中
                                        <Loader2 size={14} className="animate-spin"/>
                                    </>
                                ) : tts.enabled ? (
                                    '已开启'
                                ) : (
                                    '已关闭'
                                )}
                            </span>
                            <Switch checked={tts.enabled} onChange={handleToggleEnabled} disabled={enabling}/>
                        </div>
                    </div>
                    <div className={`transition-opacity duration-300 ${tts.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                        <h4 className="text-[15px] font-medium text-soft mb-5">选择发音人模型</h4>
                        <div className="flex gap-5 overflow-x-auto pb-6 pt-2">
                            {TTS_VOICE_MODELS.map((voice) => {
                                const isActive = activeId === voice.id
                                const isPreviewLoading = previewBusy === voice.id
                                return (
                                    <div
                                        key={voice.id}
                                        onClick={() => void handleApply(voice)}
                                        className={`shrink-0 w-48 border rounded-xl p-5 flex flex-col items-center cursor-pointer transition-all ${
                                            isActive
                                                ? 'border-accent bg-accent/5 shadow-card -translate-y-1'
                                                : 'border-hair hover:border-accent/50 hover:shadow-card bg-white'
                                        }`}
                                    >
                                        <div
                                            className="w-20 h-20 rounded-full mb-4 shadow-card border-2 border-white flex items-center justify-center text-2xl font-semibold text-ink"
                                            style={{background: voice.avatarBg || '#f0f0f0'}}
                                        >
                                            {voice.avatar}
                                        </div>
                                        <span className="font-medium text-ink text-[15px] text-center">
                                            {voice.name}
                                        </span>
                                        <span className="text-xs text-muted mt-1 mb-5 text-center truncate w-full">
                                            {voice.desc}
                                        </span>
                                        <div className="w-full flex items-center justify-center gap-2 mt-auto">
                                            {isActive ? (
                                                <span className="text-xs bg-accent text-white px-5 py-1.5 rounded-full font-medium">
                                                    使用中
                                                </span>
                                            ) : (
                                                <span className="text-xs border border-accent text-accent px-5 py-1.5 rounded-full font-medium hover:bg-accent/10 transition-colors">
                                                    应用该声音
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    void handlePreview(voice)
                                                }}
                                                disabled={!!previewBusy}
                                                title="试听"
                                                className="text-muted hover:text-accent transition-colors disabled:opacity-50"
                                            >
                                                {isPreviewLoading ? (
                                                    <Loader2 size={16} className="animate-spin"/>
                                                ) : (
                                                    <Play size={16}/>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
